import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { reconcileClikArtPlacements } from '@/lib/clikart/clikart-placement-session'
import { isGroupShape } from '@/lib/groups/group-kind'
import { doesShapeIntersectSpread } from '@/lib/photos/photo-placement-geometry'
import type { SpreadBleedSize } from './types/spread-bleed-size'

/** The ClikArt `artId` a shape displays, or null (a photo fill carries `imageId`, not `pezId`). */
export function resolvePlacedClikArtId(shape: ShapeData): string | null {
  const fill = shape.photoFill

  if (!fill || fill.imageId || !fill.pezId) return null

  return fill.pezId
}

/**
 * True when `shape` touches the part of the spread that is actually a page.
 *
 * A single-page spread (a book's first or last page) keeps the full two-page coordinate box, but
 * only one half of it is the page: the other half is drawn as grey pasteboard. Testing against the
 * whole box counted a ClikArt dropped on that grey half as placed (WMPCM-12997).
 */
function isOnSpreadPage(shape: ShapeData, spread: SpreadBleedSize): boolean {
  const { bleedWidth, bleedHeight, singlePageSide } = spread

  if (!singlePageSide) return doesShapeIntersectSpread(shape, bleedWidth, bleedHeight)

  const halfSpread = Math.floor(bleedWidth / 2)

  if (singlePageSide === 'first') {
    return doesShapeIntersectSpread(
      { ...shape, x: shape.x - halfSpread },
      bleedWidth - halfSpread,
      bleedHeight
    )
  }

  return doesShapeIntersectSpread(shape, halfSpread, bleedHeight)
}

/** ArtIds with at least one shape intersecting the spread. Pasteboard-only shapes don't count. */
export function collectPlacedClikArtIds(shapes: ShapeData[], spread: SpreadBleedSize): Set<string> {
  const ids = new Set<string>()

  for (const shape of shapes) {
    const artId = resolvePlacedClikArtId(shape)

    if (!artId) continue
    if (!isOnSpreadPage(shape, spread)) continue

    ids.add(artId)
  }

  return ids
}

/**
 * `shapes` plus any of `added` it does not already list (matched by id). The manager's list can lag
 * behind a shape a history step has just put back, so a caller that knows what it added passes it
 * here rather than trusting the list to include it.
 */
export function withShapesAdded(shapes: ShapeData[], added: ShapeData[]): ShapeData[] {
  if (added.length === 0) return shapes

  const known = new Set(shapes.map((shape) => shape.id))
  const missing = added.filter((shape) => !known.has(shape.id))

  return missing.length === 0 ? shapes : [...shapes, ...missing]
}

/**
 * `shapes` minus `removedIds` and everything nested beneath a removed group.
 *
 * The manager publishes `removeShape` on the next render, so `getShapes()` keeps listing a doomed
 * shape for the rest of the gesture removing it — and right after an undo of an add or a redo of a
 * delete. Callers discount the doomed set here instead of reading a list that still holds it.
 */
function shapesAfterRemoval(shapes: ShapeData[], removedIds: ReadonlySet<string>): ShapeData[] {
  if (removedIds.size === 0) return shapes

  const byId = new Map(shapes.map((shape) => [shape.id, shape]))
  const isRemoved = (shape: ShapeData): boolean => {
    if (removedIds.has(shape.id)) return true

    const seen = new Set<string>()
    let parentId = shape.parentGroupId

    while (parentId && !seen.has(parentId)) {
      if (removedIds.has(parentId)) return true
      seen.add(parentId)
      parentId = byId.get(parentId)?.parentGroupId
    }

    return false
  }

  return shapes.filter((shape) => !isRemoved(shape))
}

/** Sync `spreadId`'s session placements with the ClikArt currently on it — call after any add,
 *  remove, move, resize or fill change. Only that spread's own slice is touched, so an art placed
 *  on other spreads keeps its badge.
 *
 *  `removedIds` names shapes (and, for a group, everything beneath it) that are gone but that
 *  `shapes` may still list — see `shapesAfterRemoval`. */
export function reconcileClikArtPlacementsWithShapes(
  shapes: ShapeData[],
  spread: SpreadBleedSize,
  spreadId: string,
  removedIds?: ReadonlySet<string>
): void {
  const remaining = removedIds ? shapesAfterRemoval(shapes, removedIds) : shapes

  reconcileClikArtPlacements(collectPlacedClikArtIds(remaining, spread), spreadId)
}

/**
 * Re-derive `spreadId`'s placements after `removedShape` is deleted — the ClikArt counterpart of
 * `syncPhotoPlacementOverrideAfterShapeRemoved`.
 *
 * Acts for a ClikArt and for a group: `resolvePlacedClikArtId(group)` is null, but whatever sits
 * beneath it goes with it, so a deleted group can clear several cards at once.
 *
 * `alsoRemovedIds` is the rest of the same delete gesture. `onShapeDeleted` fires once per shape
 * while `shapes` still lists all of them, so each call has to discount the whole gesture — judging
 * only itself would let a later call re-mark an art an earlier one had just cleared.
 */
export function syncClikArtPlacementsAfterShapeRemoved(
  removedShape: ShapeData,
  shapes: ShapeData[],
  spread: SpreadBleedSize,
  spreadId: string,
  alsoRemovedIds?: ReadonlySet<string>
): void {
  if (!resolvePlacedClikArtId(removedShape) && !isGroupShape(removedShape)) return

  const removedIds = new Set<string>(alsoRemovedIds)

  removedIds.add(removedShape.id)
  reconcileClikArtPlacementsWithShapes(shapes, spread, spreadId, removedIds)
}
